import type { Locale, StringKey } from './i18n'

/**
 * Meeting-mode copy (Everhall Live, ADR 0013): host, participant and guest vocabulary, the same
 * copy rules as i18n.ts. A key missing here falls back to the class catalogue, so every string
 * that says class, lesson, teacher or student needs an entry (FAILURE_MODES.md, M1). Entries
 * without such words reword join instructions: meetings are joined from an invite link.
 */
export const meetingCopy: Record<Locale, Partial<Record<StringKey, string>>> = {
  en: {
    'boot.preparing': 'Preparing your meeting…',
    'boot.noLink.title': 'No meeting link',
    'boot.noLink.detail': 'Open the invite link again — it brings you here with a fresh join link.',
    'prejoin.group': 'Meeting',
    'prejoin.solo': 'Meeting',
    'prejoin.classLabel': 'Meeting',
    'waitingClass.title': "The meeting hasn't opened yet",
    'left.title': 'You left the meeting',
    'left.detail': 'Rejoin if the meeting is still running.',
    'left.rejoin': 'Rejoin meeting',
    'removed.title': 'You were removed from this meeting',
    'removed.detail': 'The host removed you. Ask them for the link again if this was a mistake.',
    'evicted.detail':
      'This meeting is now open somewhere else. Close this tab, or rejoin here to move the meeting back.',
    'error.expired.detail': 'Open the invite link again to get a fresh one.',
    'error.notStarted.title': 'This meeting has not started yet',
    'error.notStarted.detail': 'Come back closer to the start time and open the invite link again.',
    'error.ended.title': 'This meeting has ended',
    'error.ended.detail': 'Thanks for joining.',
    'error.unavailable.title': 'The meeting is not ready yet',
    'error.unknown.detail': 'Try again — if it keeps failing, open the invite link again.',
    'prejoin.noCamera':
      "This device doesn't have a camera — that's fine. Everyone will still hear you.",
    'prejoin.hostMicFault':
      'Nobody will hear you until your microphone works. You can fix it after joining.',
    'prejoin.hostMicOff': 'Your microphone is off — nobody will hear you.',
    'connection.body': 'The meeting may freeze or cut out. Use Fix now to recover the connection.',
    'help.speaker.blocked': 'Tap to let the meeting play sound',
    'help.share.board': 'Close the whiteboard so everyone can see your screen',
    'help.connection.lost': 'The meeting is reconnecting',
    'help.pip.ready': 'Ready to float the meeting',
    'mic.help.askAdult': 'Still stuck? Tell the host in the chat.',
    'role.student': 'Participant',
    'role.teacher': 'Host',
    'room.controls': 'Meeting controls',
    'audio.blocked': 'Tap to hear the meeting.',
    'chat.title': 'Meeting chat',
    'chat.dropHint': 'They will be sent to the meeting',
    'chat.shareToPlay': 'To play it for everyone, share your screen with sound.',
    'share.asked': 'Could you show your screen to the meeting?',
    'share.errUnfocused': 'Tap “Share screen” again with this meeting in front.',
    'share.noSound':
      'This share has no sound. To let everyone hear a video, share the browser tab it is playing in and turn on that tab’s sound option.',
    'share.help.system.4':
      'Quit and reopen the browser. Your meeting link still works after reopening.',
    'share.help.unfocused': 'Click the meeting window first, then share your screen.',
    'share.help.black': 'Close the whiteboard so everyone can see your screen.',
    'share.soundOn':
      'Everyone can hear the shared sound. Headphones keep your voice clear while it plays.',
    'share.stoppedByTeacher':
      'Your screen sharing has stopped. You can share again whenever the meeting needs it.',
    'pip.enter': 'Float the meeting',
    'pip.title': 'Everhall — your meeting',
    'pip.emptyTitle': 'Your meeting is still here',
    'pip.steps.generic.1': 'Click Float the meeting before you switch windows.',
    'pip.autoBlockedTitle': "Your meeting won't float automatically.",
    'pip.tipTitle': 'Tip: when you switch tabs, your meeting floats in a small window.',
    'pip.tipBody': 'Switching to another app? Click Float the meeting first.',
    'roster.title': 'In this meeting',
    'roster.openCount': 'Show people — {count} in meeting',
    'room.leave': 'Leave meeting',
    'room.leaveTitle': 'Leave the meeting?',
    'room.leaveBody':
      'Everyone will see you go. You can rejoin with the invite link while the meeting is still running.',
    'room.leaveStay': 'Stay in meeting',
    'room.waitingStudents': 'Waiting for others to join',
    'room.micBanner.host': "Nobody can hear you — your microphone isn't working.",
    'room.micBanner.student': "Nobody can hear you — your microphone isn't working.",
    'saver.autoL1': 'Saving data — other people’s videos are paused so the sound stays clear.',
    'nc.toastOn':
      'Noise cancellation is on — sounds around you (fans, street, family) are filtered out, so everyone hears only your voice.',
    'moderate.remove': 'Remove from meeting',
    'moderate.askNote':
      'Turning things off is immediate. Turning them back on is the participant’s choice.',
    'unmute.askedMic': 'The host asks you to turn your microphone on',
    'unmute.askedCamera': 'The host asks you to turn your camera on',
    'waiting.title': "You're in! Waiting for the host…",
    'waiting.sub': 'The host will be here any moment.',
    'waiting.hint2': 'Tip: tap the hand button when you want to speak.',
    'waiting.hint3': 'Tip: headphones stop echo for everyone.',
    'waiting.hint4': 'Tip: you can send a 👍 or a 🎉 to everyone.',
  },
  ar: {
    'boot.preparing': 'جارٍ تحضير الاجتماع…',
    'boot.noLink.title': 'لا يوجد رابط للاجتماع',
    'boot.noLink.detail': 'افتح رابط الدعوة مرة أخرى — سيفتح لك رابط انضمام جديدًا.',
    'prejoin.group': 'اجتماع',
    'prejoin.solo': 'اجتماع',
    'prejoin.classLabel': 'الاجتماع',
    'waitingClass.title': 'لم يُفتح الاجتماع بعد',
    'waitingClass.detail': 'سيتم الانضمام تلقائيًا فور فتح الاجتماع — ابقَ على هذه الشاشة.',
    'left.title': 'لقد خرجت من الاجتماع',
    'left.reconnectFailed': 'تعذّر علينا إعادة اتصالك بالاجتماع',
    'left.detail': 'يمكنك الانضمام مرة أخرى إذا كان الاجتماع ما زال جاريًا.',
    'left.rejoin': 'العودة إلى الاجتماع',
    'removed.title': 'تمت إزالتك من هذا الاجتماع',
    'removed.detail': 'أزالك المضيف من الاجتماع. اطلب الرابط مرة أخرى إذا كان ذلك خطأً.',
    'evicted.detail':
      'الاجتماع مفتوح الآن على جهاز آخر. أغلق هذه الصفحة، أو انضم من هنا لإرجاع الاجتماع إلى هذا الجهاز.',
    'error.expired.detail': 'افتح رابط الدعوة مرة أخرى للحصول على رابط جديد.',
    'error.notStarted.title': 'لم يبدأ الاجتماع بعد',
    'error.notStarted.detail': 'عد قبل موعد البداية بقليل وافتح رابط الدعوة مرة أخرى.',
    'error.ended.title': 'انتهى هذا الاجتماع',
    'error.ended.detail': 'شكرًا لانضمامك.',
    'error.unavailable.title': 'الاجتماع ليس جاهزًا بعد',
    'error.unknown.detail': 'حاول مرة أخرى — وإذا استمرت المشكلة افتح رابط الدعوة من جديد.',
    'prejoin.noCamera': 'لا توجد كاميرا في هذا الجهاز — لا مشكلة. سيسمعك الجميع.',
    'prejoin.hostMicFault': 'لن يسمعك أحد حتى يعمل الميكروفون. يمكنك إصلاحه بعد الانضمام.',
    'prejoin.hostMicOff': 'الميكروفون مغلق — لن يسمعك أحد.',
    'connection.body':
      'قد يتجمّد الاجتماع أو ينقطع. الإصلاح من هنا عادةً أفضل من نقل الجميع إلى تطبيق آخر.',
    'help.speaker.blocked': 'اضغط للسماح بتشغيل صوت الاجتماع',
    'help.share.board': 'أغلق السبورة حتى يرى الجميع شاشتك',
    'help.connection.lost': 'جارٍ إعادة اتصال الاجتماع',
    'help.pip.ready': 'الاجتماع جاهز ليصبح عائمًا',
    'mic.help.askAdult': 'ما زالت المشكلة؟ أخبر المضيف في الدردشة.',
    'role.student': 'مشارك',
    'role.teacher': 'المضيف',
    'room.controls': 'أدوات الاجتماع',
    'audio.blocked': 'اضغط لسماع الاجتماع.',
    'chat.title': 'دردشة الاجتماع',
    'chat.dropHint': 'سيتم إرسالها إلى الاجتماع',
    'chat.shareToPlay': 'لتشغيله للجميع، شارك شاشتك مع الصوت.',
    'share.asked': 'هل يمكنك مشاركة شاشتك مع الاجتماع؟',
    'share.errUnfocused': 'اضغط «مشاركة الشاشة» مرة أخرى وشاشة الاجتماع أمامك.',
    'share.noSound':
      'هذه المشاركة بلا صوت. لكي يسمع الجميع صوت الفيديو، تُشارَك علامة التبويب التي يعمل فيها الفيديو مع تشغيل خيار الصوت الخاص بها.',
    'share.help.system.4': 'أغلق المتصفح وافتحه من جديد. رابط الاجتماع ما زال يعمل بعد الفتح.',
    'share.help.unfocused': 'اضغط نافذة الاجتماع أولًا، ثم شارك الشاشة.',
    'share.help.black': 'أغلق السبورة حتى يرى الجميع شاشتك.',
    'share.soundOn':
      'الجميع يسمعون صوت المشاركة الآن. استخدام سماعة رأس يحافظ على وضوح الصوت أثناء تشغيله.',
    'share.stoppedByTeacher':
      'توقفت مشاركة شاشتك. يمكن مشاركتها مرة أخرى في أي وقت يحتاجها الاجتماع.',
    'pip.enter': 'اجعل الاجتماع عائمًا',
    'pip.title': 'Everhall — اجتماعك',
    'pip.emptyTitle': 'اجتماعك ما زال هنا',
    'pip.steps.generic.1': 'اضغط «اجعل الاجتماع عائمًا» قبل تغيير النافذة.',
    'pip.autoBlockedTitle': 'لن يطفو الاجتماع تلقائيًا.',
    'pip.tipTitle': 'نصيحة: عند تغيير التبويب، يطفو الاجتماع في نافذة صغيرة.',
    'pip.tipBody': 'هل ستنتقل لتطبيق آخر؟ اضغط «اجعل الاجتماع عائمًا» أولًا.',
    'roster.title': 'في هذا الاجتماع',
    'roster.openCount': 'إظهار المشاركين — {count} في الاجتماع',
    'room.leave': 'مغادرة الاجتماع',
    'room.leaveTitle': 'هل تريد مغادرة الاجتماع؟',
    'room.leaveBody': 'سيرى الجميع أنك غادرت. يمكنك الرجوع برابط الدعوة ما دام الاجتماع مستمرًا.',
    'room.leaveStay': 'البقاء في الاجتماع',
    'room.waitingStudents': 'في انتظار انضمام الآخرين',
    'room.micBanner.host': 'لا يسمعك أحد — الميكروفون لا يعمل.',
    'room.micBanner.student': 'لا يسمعك أحد — الميكروفون لا يعمل.',
    'saver.autoL1': 'نوفر البيانات — أوقفنا فيديوهات الآخرين ليبقى الصوت واضحًا.',
    'nc.toastOn': 'إلغاء الضوضاء يعمل الآن — تُرشَّح الأصوات من حولك ولا يصل للآخرين سوى صوتك.',
    'moderate.remove': 'إزالة من الاجتماع',
    'moderate.askNote': 'الإيقاف يحدث فورًا، وإعادة التشغيل تبقى قرار المشارك.',
    'unmute.askedMic': 'يطلب المضيف تشغيل الميكروفون',
    'unmute.askedCamera': 'يطلب المضيف تشغيل الكاميرا',
    'waiting.title': 'أنت في الاجتماع! في انتظار المضيف…',
    'waiting.sub': 'سيصل المضيف في أي لحظة.',
    'waiting.hint2': 'نصيحة: اضغط زر اليد عندما تريد التحدث.',
    'waiting.hint3': 'نصيحة: سماعة الرأس تمنع صدى الصوت عن الجميع.',
    'waiting.hint4': 'نصيحة: يمكنك إرسال 👍 أو 🎉 للجميع.',
  },
}
